/**
 * Handles dragging tasks from the grid onto the schedule
 */
import {DragHelper, MessageDialog} from "@bryntum/schedulerpro";
import bgContrastHelper, { taskRenderer } from "../../../other/utils/bgUtils.jsx";
import {getWorkingRangesInScheduler} from "../../../other/utils/schedulerUtils.jsx";
import { getSetting } from "../../../react/context/bryntumContext.jsx";
import { ViewType } from "../../../other/constants/OtherKeys.jsx";
import { MIXED_TASK_COLOR, MIXED_TASK_STYLE } from "../../../other/constants/TaskTypes.jsx";

export default class TaskDrag extends DragHelper {
  static get configurable() {
    return {
      // Don't drag the actual row element, clone it
      cloneTarget: true,
      // We size the cloned element manually
      autoSizeClonedTarget: false,
      // Only allow drops on the schedule area (same as Bryntum example)
      dropTargetSelector: ".b-timeline-sub-grid",
      // Only allow drag regular rows (target the row itself, not the event inside)
      targetSelector: ".b-grid-row:not(.b-group-row)",
      // Call our functions
      callOnFunctions: true,

      listeners: {
        beforeDragStart: "onTaskBeforeDragStart",
        dragstart: "onTaskDragStart",
        drag: "onDrag",
        drop: "onTaskDrop",
      }
    };
  }

  // Prevent crashes when dragging from TaskFinder after navigating away from Schedule page
  onTaskBeforeDragStart() {
    return !this.schedule?.isDestroyed;
  }

  createProxy(element) {
    const { schedule } = this,
      task = this.grid.getRecordFromElement(element);

    if (!task) {
      console.error("❌ No task found for proxy creation");
      return null;
    }

    const globalTaskColor = getSetting("taskColor", MIXED_TASK_COLOR, true);
    const globalTaskStyle = getSetting("taskStyle", MIXED_TASK_STYLE, true);
    const viewType = getSetting("viewType", ViewType.normal);

    console.log(globalTaskColor, globalTaskStyle, viewType);

    // Create a temporary container to render the event
    const tempContainer = document.createElement('div');
    const renderedHTML = taskRenderer(
      task,
      globalTaskColor,
      globalTaskStyle,
      viewType,
      schedule.rowHeight,
      schedule.resourceMargin
    );
    tempContainer.innerHTML = renderedHTML;

    // Get the full rendered structure (b-sch-horizontal wrapper)
    const fullStructure = tempContainer.firstElementChild;

    if (!fullStructure) {
      console.error("❌ No rendered structure found in taskRenderer output");
      return null;
    }

    // Clone the full structure to use as proxy
    const proxy = fullStructure.cloneNode(true);

    // Add additional classes for drag state
    proxy.classList.add('b-dragging-task');

    // Ensure the proxy has proper display and positioning
    proxy.style.display = 'flex';
    proxy.style.alignItems = 'center';

    // Style the proxy to look like an event bar
    if (schedule.isHorizontal) {
      proxy.style.height = `${schedule.rowHeight - (2 * schedule.resourceMargin)}px`;
      proxy.style.width = "auto";
    } else {
      proxy.style.width = `${schedule.resourceColumnWidth}px`;
      proxy.style.height = "auto";
    }

    const eventElement = proxy.querySelector('.b-sch-event');
    const contrastColor = bgContrastHelper(globalTaskColor || task.color, "#fff", "#000");
    eventElement.style.backgroundColor = task.color;
    eventElement.style.color = contrastColor || "#000";

    return proxy;
  }

  onTaskDragStart({ context }) {
    const { schedule } = this,
      proxy = context.element,
      task = this.grid.getRecordFromElement(context.grabbed);

    if (!task) {
      this.abort();
      return;
    }

    // Save a reference to the task being dragged
    context.task = task;

    // Disable pointer events on event wrap to allow drop target detection
    const eventWrap = proxy.querySelector(".b-sch-event-wrap");
    if (eventWrap) {
      eventWrap.style["pointer-events"] = "none";
    }

    // Enable scrolling while dragging
    schedule.enableScrollingCloseToEdges(schedule.timeAxisSubGrid);

    // Ensure proxy is visible with high z-index
    proxy.style.zIndex = 999999;
    proxy.style.position = "fixed";

    // Prevent tooltips from showing while dragging
    schedule.element.classList.add("b-dragging-event");

    if (schedule.features.eventTooltip) {
      schedule.features.eventTooltip.disabled = true;
    }
  }

  onDrag({ event, context }) {
    const { schedule } = this,
      { task, target } = context;

    if (!task) {
      context.valid = false;
      return;
    }

    const startDate = schedule.getDateFromCoordinate(event.clientX, "round", false, true);
    const resource = target && schedule.resolveResourceRecord(target, [event.offsetX, event.offsetY]);

    // Validate drop location
    const isValid = Boolean(startDate && resource) && !(resource?.isGroupHeader || resource?.isSpecialRow);

    // Set valid as TRUE (Bryntum's DragHelper has issues with context.valid persisting)
    // We use our own validation flag instead
    context.valid = true;
    context.isValidDrop = isValid;

    // Save reference for onTaskDrop
    context.resource = resource;
    context.startDate = startDate;

    // Update visual feedback
    if (isValid) {
      context.element.classList.remove("b-invalid");
      context.element.classList.add("b-valid");
    } else {
      context.element.classList.remove("b-valid");
      context.element.classList.add("b-invalid");
    }
  }

  async onTaskDrop({ context, event }) {
    const { schedule } = this,
      { target, task, isValidDrop } = context;

    schedule.disableScrollingCloseToEdges(schedule.timeAxisSubGrid);

    if (schedule.features.eventTooltip) {
      schedule.features.eventTooltip.disabled = false;
    }

    const targetResource = target && schedule.resolveResourceRecord(target, [event.offsetX, event.offsetY]);

    if (isValidDrop && target && targetResource) {
      const startDate = schedule.getDateFromCoordinate(event.clientX, "floor", false);

      if (!startDate) {
        this.abort();
        schedule.element.classList.remove("b-dragging-event");
        return;
      }

      // Hide proxy immediately to prevent animation back to grid
      if (context.element) {
        context.element.style.display = 'none';
      }

      const state = schedule.project.crudStores.map(s => {
        const state = s.store.reapplyFilterOnUpdate;
        s.store.reapplyFilterOnUpdate = false;
        return state;
      });
      const promise = schedule.project.commitAsync();

      const eventsToAdd = [], assignmentsToAdd = [];
      getWorkingRangesInScheduler(startDate, schedule).forEach((dateRange) => {
        const newEvent = schedule.eventStore.createRecord({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          taskId: task.id
        });
        const newAssignment = schedule.assignmentStore.createRecord({
          resourceId: targetResource.id,
          eventId: newEvent.id
        });
        eventsToAdd.push(newEvent);
        assignmentsToAdd.push(newAssignment);
      });

      if (eventsToAdd.length >= 50) {
        const response = await MessageDialog.confirm({
          okButton: "Proceed",
          cancelButton: "Cancel",
          title: `Creating ${eventsToAdd.length} task events`,
          message: `${eventsToAdd.length} are being created at the same time which could take some to resolve`
        });

        if (response.okButton) {
          schedule.eventStore.add(eventsToAdd);
          schedule.assignmentStore.add(assignmentsToAdd);
        } else {
          // User cancelled - restore proxy and abort
          if (context.element) {
            context.element.style.display = 'flex';
          }
          this.abort();
          schedule.element.classList.remove("b-dragging-event");
          return;
        }
      } else {
        schedule.eventStore.add(eventsToAdd);
        schedule.assignmentStore.add(assignmentsToAdd);
      }

      await promise;
      schedule.project.crudStores.forEach((s, index) => {
        s.store.reapplyFilterOnUpdate = state[index];
        s.store.filter();
      });

      // Finalize without revert animation
      context.finalize(false);
    } else {
      this.abort();
    }

    schedule.element.classList.remove("b-dragging-event");
  }
}
